import React, { useState, useEffect } from 'react';
import { CheckCircle, ChevronLeft } from 'lucide-react';
import PhoneOtpSection from './PhoneOtpSection';
import EmailOtpSection from './EmailOtpSection';
import { apiRequest } from '../../../services/api';

const VerifiedBadge = ({ label }) => (
  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
    <span className="text-sm font-medium text-emerald-800">{label} — Verified</span>
  </div>
);

const Step2VerifyIdentity = ({ phone, email, smsConsentGranted = false, onNext, onBack }) => {
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [recovering, setRecovering] = useState(true);

  useEffect(() => {
    recoverState();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const recoverState = async () => {
    try {
      const requests = [
        apiRequest('/api/v1/guides/signup/verification-status', { method: 'GET' }),
      ];
      if (smsConsentGranted) {
        requests.unshift(apiRequest('/api/v1/communications/phone-verification/status', { method: 'GET' }));
      }
      if (smsConsentGranted) {
        const [phoneResult, emailResult] = await Promise.all(requests);
        setPhoneVerified(phoneResult.data?.verified === true);
        setEmailVerified(emailResult.data?.emailVerified === true);
      } else {
        const [emailResult] = await Promise.all(requests);
        setEmailVerified(emailResult.data?.emailVerified === true);
      }
    } catch {
      // Start unverified — user will complete the OTP flows fresh
    } finally {
      setRecovering(false);
    }
  };

  const canContinue = smsConsentGranted
    ? phoneVerified && emailVerified
    : emailVerified;

  if (recovering) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          {smsConsentGranted ? 'Verify your email and phone' : 'Verify your email'}
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          {smsConsentGranted
            ? 'Confirm your phone number and email to continue.'
            : 'Confirm your email address to continue.'}
        </p>
      </div>

      {smsConsentGranted && (
        phoneVerified ? (
          <VerifiedBadge label="Phone" />
        ) : (
          <PhoneOtpSection
            phone={phone}
            onVerified={() => setPhoneVerified(true)}
          />
        )
      )}

      {(!smsConsentGranted || phoneVerified) && (
        emailVerified ? (
          <VerifiedBadge label="Email" />
        ) : (
          <EmailOtpSection
            email={email}
            onVerified={() => setEmailVerified(true)}
          />
        )
      )}

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canContinue}
          className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          Continue to Step 3
        </button>
      </div>
    </div>
  );
};

export default Step2VerifyIdentity;
